import React, { useState } from 'react'
import {
  Card,
  Badge,
  Avatar,
  AvatarFallback,
  TwEmoji,
} from '@t-one-internal/design-system'
import { useRecognitionStore } from '../../../stores/recognitionStore'
import { recognitionService } from '../../../services/recognitionService'
import type { Recognition, Reactions, Employee } from '../../../types/recognition'

// ─── Type theming with Emojis ────────────────────────────────────────
const TYPE_THEME: Record<string, {
  bg: string
  emoji: string
  label: string
  badgeVariant: 'default' | 'secondary' | 'success' | 'warning' | 'info' | 'outline'
}> = {
  kudos: { bg: 'bg-amber-50', emoji: '👏', label: 'Kudos', badgeVariant: 'warning' },
  spot_award: { bg: 'bg-rose-50', emoji: '🏅', label: 'Spot Award', badgeVariant: 'default' },
  shoutout: { bg: 'bg-sky-50', emoji: '📣', label: 'Shout-out', badgeVariant: 'info' },
  milestone: { bg: 'bg-emerald-50', emoji: '🏆', label: 'Milestone', badgeVariant: 'success' },
  thankyou: { bg: 'bg-violet-50', emoji: '💜', label: 'Thank You', badgeVariant: 'secondary' },
}

// ─── Reaction config with Emojis ─────────────────────────────────────
const REACTION_CONFIG: { key: keyof Reactions; emoji: string; activeColor: string; label: string }[] = [
  { key: 'clap', emoji: '👏', activeColor: 'text-amber-500', label: 'Clap' },
  { key: 'heart', emoji: '❤️', activeColor: 'text-rose-500', label: 'Love' },
  { key: 'celebrate', emoji: '🎉', activeColor: 'text-purple-500', label: 'Celebrate' },
  { key: 'fire', emoji: '🔥', activeColor: 'text-orange-500', label: 'Fire' },
]

import { hashValueColor } from '../utils/valueColors'

const MAX_VISIBLE_AVATARS = 3

// ─── Hover tooltip showing all recipients (only for 4+ recipients) ──
function RecipientsTooltip({ recipients, children }: { recipients: Employee[]; children: React.ReactNode }) {
  const [show, setShow] = useState(false)

  if (recipients.length <= MAX_VISIBLE_AVATARS) return <>{children}</>

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div className="absolute z-30 left-1/2 -translate-x-1/2 top-full mt-2 w-56 rounded-xl border border-border bg-popover shadow-xl p-3 animate-in fade-in-0 zoom-in-95 duration-150">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            All Recipients ({recipients.length})
          </p>
          <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto">
            {recipients.map((r) => (
              <div key={r.id} className="flex items-center gap-2 py-1 px-1 rounded-md hover:bg-accent/50 transition-colors">
                <Avatar className="w-6 h-6 text-[9px] shrink-0">
                  <AvatarFallback className="font-semibold bg-primary/20 text-primary">
                    {makeInitials(r.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs font-medium text-foreground truncate">{r.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface RecognitionCardProps {
  recognition: Recognition
  isHighlighted?: boolean
}

const UNKNOWN_EMPLOYEE: Employee = { id: '', name: 'Unknown', dept: '', team: '', title: '', initials: '?', avatar: null }

function makeInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '?'
}

function recipientNames(recipients: Employee[]): string {
  if (recipients.length === 0) return 'Unknown'
  if (recipients.length === 1) return recipients[0].name
  if (recipients.length === 2) return `${recipients[0].name} & ${recipients[1].name}`
  if (recipients.length === 3) return `${recipients[0].name}, ${recipients[1].name} & ${recipients[2].name}`
  return `${recipients[0].name}, ${recipients[1].name}, ${recipients[2].name} & ${recipients.length - 3} others`
}

// Shared small avatar used throughout the card
function UserChip({ name, size = 'sm' }: { name: string; size?: 'sm' | 'md' | 'lg' | 'xl' | 'xs' | '2xl' }) {
  const sizeClass =
    size === 'xs' ? 'w-5 h-5 text-[9px]'
      : size === 'sm' ? 'w-7 h-7 text-[10px]'
        : size === 'xl' ? 'w-10 h-10 text-xs'
          : size === '2xl' ? 'w-14 h-14 text-sm'
            : 'w-8 h-8 text-xs'

  return (
    <Avatar className={sizeClass}>
      <AvatarFallback className="font-semibold bg-primary/20 text-primary">
        {makeInitials(name)}
      </AvatarFallback>
    </Avatar>
  )
}

const RecognitionCard: React.FC<RecognitionCardProps> = ({ recognition, isHighlighted }) => {
  const updateReaction = useRecognitionStore((s) => s.updateRecognitionReaction)
  const cardRef = React.useRef<HTMLDivElement>(null)

  // Auto-scroll to this card when it becomes highlighted
  React.useEffect(() => {
    if (isHighlighted && cardRef.current) {
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 300) // Masonry needs a bit more time to settle
    }
  }, [isHighlighted])

  const sender = recognition.sender ?? UNKNOWN_EMPLOYEE
  const recipients = recognition.recipients ?? []
  const reactions = recognition.reactions ?? { clap: 0, heart: 0, celebrate: 0, fire: 0 }
  const theme = TYPE_THEME[recognition.type] || TYPE_THEME.kudos

  const handleReaction = async (key: keyof Reactions) => {
    try {
      const { added } = await recognitionService.addReaction({
        recognition_id: recognition.id,
        reaction_type: key,
      })
      updateReaction(recognition.id, key, added ? 1 : -1)
    } catch {
      // Silently fail
    }
  }

  return (
    <>
      <div ref={cardRef} className={isHighlighted ? 'relative z-20' : ''}>
        <Card className={`overflow-hidden transition-all duration-500 border-2 ${isHighlighted
          ? 'border-primary ring-4 ring-primary/20 shadow-2xl shadow-primary/30 scale-[1.02] active-highlight animate-pulse-subtle'
          : 'border-border/60 hover:shadow-md'
          }`}>
          {/* ─── Colored Header ─── */}
          <div className={`relative px-5 pt-4 pb-5 ${theme.bg}`}>
            {/* Top row */}
            <div className="flex items-center justify-between mb-3">
              <TwEmoji emoji={theme.emoji} size={28} />
              <span className="text-xs text-muted-foreground">{recognition.time}</span>
            </div>

            {/* Recipient Avatars */}
            <div className="flex justify-center">
              {recipients.length <= 1 ? (
                <UserChip name={(recipients[0] ?? UNKNOWN_EMPLOYEE).name} size="2xl" />
              ) : (
                <RecipientsTooltip recipients={recipients}>
                  <div className="flex -space-x-2 cursor-pointer">
                    {recipients.slice(0, MAX_VISIBLE_AVATARS).map((r) => (
                      <div key={r.id} className="border-[3px] border-white rounded-full">
                        <UserChip name={r.name} size="xl" />
                      </div>
                    ))}
                    {recipients.length > MAX_VISIBLE_AVATARS && (
                      <div className="border-[3px] border-white rounded-full">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold bg-primary/20 text-primary">
                          +{recipients.length - MAX_VISIBLE_AVATARS}
                        </div>
                      </div>
                    )}
                  </div>
                </RecipientsTooltip>
              )}
            </div>

            <p className="text-center font-bold text-foreground mt-2.5 text-sm">
              {recipientNames(recipients)}
            </p>
          </div>

          {/* ─── Card Body ─── */}
          <div className="px-5 py-4">
            {/* Sender */}
            <div className="flex items-center gap-2 mb-2">
              <UserChip name={sender.name} size="xs" />
              <span className="text-sm font-medium text-foreground">
                {sender.name}
              </span>
            </div>

            {/* Message */}
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              {recognition.message}
            </p>

            {/* Tags row — value badge + optional points  */}
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              {recognition.value ? (() => {
                const vc = hashValueColor(recognition.value)
                return (
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                    style={{ backgroundColor: vc.bg, color: vc.text }}
                  >
                    {recognition.value}
                  </span>
                )
              })() : null}
              {recognition.points ? (
                <Badge variant="warning" className="rounded-full text-[11px] font-bold">
                  +{recognition.points} pts
                </Badge>
              ) : null}
            </div>

            {/* ─── Reactions ─── */}
            <div className="flex items-center gap-3 pt-3 border-t border-border">
              {REACTION_CONFIG.map(({ key, emoji, activeColor, label }) => {
                const count = reactions[key]
                const isActive = count > 0
                return (
                  <button
                    key={key}
                    title={label}
                    onClick={() => handleReaction(key)}
                    className={`flex items-center gap-1 transition-all ${isActive
                      ? activeColor
                      : 'text-muted-foreground/50 hover:text-muted-foreground grayscale opacity-60 hover:grayscale-0 hover:opacity-100'
                      }`}
                  >
                    <TwEmoji emoji={emoji} size={16} />
                    {count > 0 && (
                      <span className="text-xs font-medium">{count}</span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </Card>
      </div>

    </>
  )
}

export default RecognitionCard
